import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { DailyInspection } from '@obraplan/core/domain/planning/daily-inspection';
import type { DailyInspectionId } from '@obraplan/core/domain/shared/ids';
import { PENDING_INSPECTION_ID, type OutboxEntry } from '@/app/outbox-types';

/**
 * O cartão com o que está na fila do aparelho por cima (Fase 43, RN-604, ADR-195).
 *
 * Sem isto, quem confirma sem sinal veria o cartão ainda pendente, e confirmaria de novo. Cada
 * operação sabe o que muda no dia da pessoa, e a tela mostra o efeito esperado com a marca
 * "aguardando envio". Quando a operação sobe, a leitura nova do servidor substitui esta, e a
 * marca sai.
 *
 * É previsão, e não regra: quem decide é o servidor, na hora em que a operação chegar. A
 * recusa volta para o cartão com o motivo (RN-608).
 */

/** O aviso de quem gravou sem sinal (RN-603): nada se perdeu, e nada falta fazer. */
export const GUARDADO_NO_APARELHO = 'Guardado no aparelho — sobe sozinho quando o sinal voltar.';

/** O que o cartão mostra sobre a fila daquele dia. */
export interface SyncDoCartao {
  readonly state: 'pending' | 'rejected';
  /** O motivo do servidor, na recusa. */
  readonly message?: string;
  /** O registro recusado — é ele que se reenvia ou descarta. */
  readonly rejectedId?: string;
  /** Quantos registros deste dia esperam. */
  readonly count: number;
}

function conferenciaDe(line: InspectionLine, operation: OutboxEntry['operation']): DailyInspection {
  if (line.inspection !== undefined) return line.inspection;
  // A conferência que ainda não existe no servidor: a fila a abre quando subir (RN-100).
  return {
    id: PENDING_INSPECTION_ID as DailyInspectionId,
    projectId: operation.projectId,
    resourceId: line.resource.resourceId,
    date: operation.date,
    status: 'PENDING',
    plannedQuarterHours: line.plannedQuarterHours,
    reopenCount: 0,
    version: 0,
    createdAt: '',
    updatedAt: '',
  } as DailyInspection;
}

/** A linha do dia com as operações da fila aplicadas, na ordem em que foram feitas. */
export function comAFila(line: InspectionLine, entries: readonly OutboxEntry[]): InspectionLine {
  let atual = line;
  for (const { operation } of entries) {
    if (!eDaPessoa(operation, line.resource.resourceId)) continue;
    switch (operation.kind) {
      case 'ausencia': {
        const { notes: _notes, ...base } = conferenciaDe(atual, operation);
        atual = {
          ...atual,
          inspection: {
            ...base,
            status: 'ABSENT',
            absenceReason: operation.reason,
            executedQuarterHours: 0,
          },
        };
        break;
      }
      case 'reabrir': {
        const {
          absenceReason: _motivo,
          executedQuarterHours: _horas,
          ...base
        } = conferenciaDe(atual, operation);
        atual = { ...atual, inspection: { ...base, status: 'PENDING' } };
        break;
      }
      case 'medir': {
        const horas = new Map(operation.adjustments.map((a) => [a.allocationId, a.quarterHours]));
        const medidas = new Map(
          operation.measurements.map((m) => [m.allocationId, m.executedQuantity]),
        );
        const allocations = atual.allocations.map((item) => {
          const quarterHours = horas.get(item.allocation.id) ?? item.allocation.quarterHours;
          const medida = medidas.get(item.allocation.id);
          const { executedQuantity: _antes, ...semMedida } = item.allocation;
          return {
            ...item,
            allocation: {
              ...(medida === undefined ? item.allocation : semMedida),
              quarterHours,
              ...(medida === undefined || medida === null ? {} : { executedQuantity: medida }),
            },
          };
        });
        const status =
          operation.adjustments.length > 0
            ? 'ADJUSTED'
            : operation.confirm
              ? 'CONFIRMED'
              : (atual.inspection?.status ?? 'PENDING');
        atual = {
          ...atual,
          allocations,
          ...(status === (atual.inspection?.status ?? 'PENDING')
            ? {}
            : {
                inspection: {
                  ...conferenciaDe(atual, operation),
                  status,
                  executedQuarterHours: allocations.reduce(
                    (soma, item) => soma + item.allocation.quarterHours,
                    0,
                  ),
                },
              }),
        };
        break;
      }
      case 'fotos-da-conferencia':
      case 'fotos-da-nc':
      case 'remover-foto':
        // A foto não muda o veredito: o cartão só ganha a marca de que algo espera, e a galeria
        // mostra a foto que vai entrar ou sair (`fotos-na-fila.ts`).
        break;
      case 'resolver-nc':
      case 'rdo-emitir':
      case 'rdo-revisar':
        // A NC e o RDO têm a sua chave na fila (RN-623 a RN-625).
        break;
      case 'medir-equipe':
      case 'fotos-da-equipe':
      case 'rdo-clima':
      case 'rdo-tarefas':
      case 'rdo-ocorrencias':
      case 'rdo-efetivo':
      case 'rdo-equipamento':
      case 'rdo-fotos':
      case 'comodos':
      case 'pecas-da-frente':
        // A equipe, o RDO e a frente têm a sua chave na fila, e não chegam ao cartão da pessoa.
        break;
      case 'lancar-trabalho':
      case 'lancar-trabalho-equipe': {
        // O lançamento cria a alocação no servidor e confirma o dia da pessoa (RN-225, RN-644): a
        // aberta sai confirmada; a encerrada reabre e sai ajustada. O cartão mostra isso já — sem
        // isso, o ajuste feito depois, sem sinal, voltava recusado ("conferência encerrada").
        // A ausente o servidor recusa, e o cartão fica como está até a recusa voltar.
        const antes = atual.inspection?.status ?? 'PENDING';
        if (antes === 'ABSENT') break;
        // A troca (RN-645) tira a frente errada, como o "Tirar", e reabre a encerrada antes de
        // lançar a certa: o dia sai confirmado, e não ajustado.
        const trocada =
          operation.kind === 'lancar-trabalho' ? operation.input.replaces?.allocationId : undefined;
        atual = {
          ...atual,
          allocations:
            trocada === undefined
              ? atual.allocations
              : atual.allocations.filter((item) => item.allocation.id !== trocada),
          inspection: {
            ...conferenciaDe(atual, operation),
            status: antes === 'PENDING' || trocada !== undefined ? 'CONFIRMED' : 'ADJUSTED',
          },
        };
        break;
      }
      case 'tirar-frente':
        // A frente sai do cartão já (RN-620). Sem nenhuma, a pessoa sai do dia (RN-631).
        atual = {
          ...atual,
          allocations: atual.allocations.filter(
            (item) => item.allocation.id !== operation.allocationId,
          ),
        };
        break;
    }
  }
  return atual;
}

/** A marca do cartão: o recusado manda, porque é ele que pede a pessoa (RN-608). */
export function syncDoCartao(entries: readonly OutboxEntry[]): SyncDoCartao | undefined {
  if (entries.length === 0) return undefined;
  const recusado = entries.find((entry) => entry.state === 'rejected');
  if (recusado === undefined) return { state: 'pending', count: entries.length };
  return {
    state: 'rejected',
    count: entries.length,
    rejectedId: recusado.id,
    ...(recusado.error === undefined ? {} : { message: recusado.error.message }),
  };
}

/** As entradas da fila por pessoa. */
export function filaPorPessoa(
  entries: readonly OutboxEntry[],
): ReadonlyMap<string, readonly OutboxEntry[]> {
  const mapa = new Map<string, OutboxEntry[]>();
  const juntar = (chave: string, entry: OutboxEntry): void => {
    const lista = mapa.get(chave) ?? [];
    lista.push(entry);
    mapa.set(chave, lista);
  };
  for (const entry of entries) {
    juntar(entry.operation.resourceId, entry);
    // O lançamento da equipe que espera confirma o dia de cada membro (RN-644): ele chega ao
    // cartão de cada um. O recusado fica só no aviso do lançamento, que é onde se resolve.
    if (entry.operation.kind === 'lancar-trabalho-equipe' && entry.state === 'pending') {
      for (const membro of entry.operation.input.members) juntar(membro.resourceId, entry);
    }
  }
  return mapa;
}

/** Se a operação mexe no dia desta pessoa: a dela, ou o lançamento da equipe em que ela está. */
function eDaPessoa(operation: OutboxEntry['operation'], resourceId: string): boolean {
  if (operation.resourceId === resourceId) return true;
  return (
    operation.kind === 'lancar-trabalho-equipe' &&
    operation.input.members.some((membro) => membro.resourceId === resourceId)
  );
}

/** Dois minutos: abaixo disso, a hora do tablet e a do servidor são a mesma gravação. */
const FEITA_SEM_SINAL_MS = 2 * 60 * 1000;

/**
 * A hora da alteração. A que veio da fila do aparelho mostra as duas (Fase 43, D4, RN-607):
 * quando foi feita no tablet e quando chegou ao servidor.
 */
export function quandoFoiFeita(change: {
  readonly occurredAt: string;
  readonly deviceOccurredAt?: string;
}): string {
  const servidor = new Date(change.occurredAt).toLocaleString('pt-BR');
  if (change.deviceOccurredAt === undefined) return servidor;
  const diferenca = Date.parse(change.occurredAt) - Date.parse(change.deviceOccurredAt);
  if (Math.abs(diferenca) < FEITA_SEM_SINAL_MS) return servidor;
  const tablet = new Date(change.deviceOccurredAt).toLocaleString('pt-BR');
  return `feita às ${tablet} sem conexão · enviada às ${servidor}`;
}
